"use client";

// Client-only wrapper around `@aio/platform-table`'s `DataTable`.
//
// The substrate's list page factory is a server-safe function — it builds
// columns from an `EntityDefinition` and returns a server component that
// renders the table. But `DataTable` requires a `rowId: (row) => string`
// callback at the JSX site, and you can't pass a function from a server
// component into a client component as a prop. Wrapping the table here
// keeps the function defined on the client side and lets the server pass
// only serializable props (rows + columns) across the boundary.
//
// Activation → detail navigation
//   The factory passes a `detailHrefPrefix` so this wrapper can build a
//   per-row href on activation (click anywhere outside the selection cell,
//   or focus + Enter). We use a plain anchor click via `window.location`
//   rather than `next/router`'s push because the wrapper is a client
//   component but the server-rendered list page treats the table as a
//   nested island — a top-level navigation here matches the
//   `/demo/.../m/.../e/.../<id>` route layout and re-runs `resolveCompany`.
//
// i18n + formatting (October 2026 fix)
//   Column `header` arrives as a dotted i18n KEY (e.g. `fleet.machine.plate`).
//   We resolve it via `useTranslations()` against the merged module bundle
//   wired in `apps/web/app/modules-bootstrap.ts`. Date / timestamp values
//   come down the wire as ISO strings (Next 16's serializer rejects raw
//   Dates), so we coerce them back into Date instances on the client and
//   pipe `formatDate` from platform-i18n into the table's formatter bundle —
//   that's what fixes the "raw Date.toString" QA finding without forcing
//   every column author to write a `cell` override.

import * as React from "react";
import { DataTable } from "@aio/platform-table";
import type { ColumnDef, FormatterBundle } from "@aio/platform-table";
import {
  useLocale,
  useT,
  formatNumber,
  formatCurrency,
  formatDate,
} from "@aio/platform-i18n";

export interface EntityDataTableProps<TRow extends { id: string }> {
  rows: TRow[];
  columns: ColumnDef<TRow>[];
  /**
   * URL prefix for the detail page; the wrapper appends `/<row.id>` on
   * row activation. Optional — when omitted, activation is a no-op so
   * legacy callers that haven't yet wired a detail route stay safe.
   */
  detailHrefPrefix?: string;
}

/** Match strings that look like ISO 8601 dates, so we know when to coerce
 *  back into a Date instance for `type: "date" | "datetime"` columns. */
function isIsoDateLike(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:\d{2}))?$/.test(s);
}

/** Build a formatter bundle bound to platform-i18n's locale-aware
 *  formatters. Without this the DataTable falls back to native Intl with
 *  the *table*'s `locale` prop — which is ok for digits but ignores the
 *  AIO project's `INTL_LOCALE` map (sr-Latn-RS instead of sr-Latn). */
function buildFormatters(
  locale: "sr-Latn" | "sr-Cyrl" | "en",
): FormatterBundle {
  return {
    number: (_loc, value, options) => formatNumber(locale, value, options),
    currency: (_loc, value, currency) => formatCurrency(locale, value, currency),
    date: (_loc, value, options) => formatDate(locale, value, options),
  };
}

export function EntityDataTable<TRow extends { id: string }>({
  rows,
  columns,
  detailHrefPrefix,
}: EntityDataTableProps<TRow>): React.ReactElement {
  const t = useT();
  const { locale } = useLocale();

  // Translate every column's `header` from a key into the localized string.
  // `next-intl` returns the raw key when missing, so an unfilled module
  // bundle degrades gracefully (the user sees `fleet.machine.plate` instead
  // of crashing).
  //
  // For `enum` columns, the substrate seeds `enumLabels` with i18n KEYS
  // (e.g. `platform.lifecycle.state.draft`) instead of pre-resolved strings
  // because the server-side page factory has no access to `t()`. We resolve
  // each label here so the table's default enum renderer (which simply
  // looks up `enumLabels[value]`) prints the translated string. When the
  // bundle lacks an entry, `useT()` returns the raw key — we treat that
  // as "fall back to the raw enum value" so the UI still reads cleanly.
  const translatedColumns = React.useMemo<ColumnDef<TRow>[]>(
    () =>
      columns.map((c) => {
        const next: ColumnDef<TRow> = { ...c, header: t(c.header) };
        if (c.type === "enum" && c.enumLabels) {
          const labels: Record<string, string> = {};
          for (const [value, key] of Object.entries(c.enumLabels)) {
            const resolved = t(key);
            labels[value] = resolved === key ? value : resolved;
          }
          next.enumLabels = labels;
        }
        return next;
      }),
    [columns, t],
  );

  // Coerce ISO date strings back into Date instances for date-typed columns.
  // The list-page factory now annotates `t.timestamp()` fields with
  // `type: "datetime"` so the table's formatter knows to call `formatters.date`
  // — but the formatter contract requires a Date instance. We do this once
  // per render up-front so every cell renderer (and exporter) sees the
  // right shape.
  const dateColumnKeys = React.useMemo(() => {
    const keys = new Set<string>();
    for (const c of columns) {
      if (c.type === "date" || c.type === "datetime") keys.add(c.key);
    }
    return keys;
  }, [columns]);

  const coercedRows = React.useMemo<TRow[]>(() => {
    if (dateColumnKeys.size === 0) return rows;
    return rows.map((row) => {
      const r = row as Record<string, unknown>;
      let mutated: Record<string, unknown> | null = null;
      for (const key of dateColumnKeys) {
        const v = r[key];
        if (typeof v === "string" && isIsoDateLike(v)) {
          const d = new Date(v);
          if (!Number.isNaN(d.getTime())) {
            mutated = mutated ?? { ...r };
            mutated[key] = d;
          }
        }
      }
      return (mutated ?? r) as TRow;
    });
  }, [rows, dateColumnKeys]);

  const formatters = React.useMemo(() => buildFormatters(locale), [locale]);

  const onRowActivate = React.useCallback(
    (row: TRow) => {
      if (!detailHrefPrefix) return;
      window.location.assign(`${detailHrefPrefix}/${row.id}`);
    },
    [detailHrefPrefix],
  );
  return (
    <DataTable
      data={coercedRows}
      columns={translatedColumns}
      rowId={(r) => r.id}
      locale={locale}
      formatters={formatters}
      {...(detailHrefPrefix ? { onRowActivate } : {})}
    />
  );
}
